/**
 * Data Row Component
 *
 * Renders a single data row in the table.
 */
import { flexRender } from "@tanstack/react-table";
import { memo } from "react";
import { toast } from "sonner";
import { cn } from "@/shared/lib/utils";
import { getCommonPinningStyles, mergeRowProps } from "../utils";
import { CopyableCell } from "./CopyableCell";
import { TruncateWrapper } from "./TruncateWrapper";
import type { AppCell } from "../features";
import type { DataRowProps, TanStackColumn } from "../types";
import type { RowData } from "@tanstack/react-table";

export const DataRow = <T extends RowData>({
  row,
  onRowClick,
  getRowClassName,
  isHighlightedRow,
  highlightedRowId,
  tooltipClassName,
  enableColumnPinning,
  getRowProps,
}: DataRowProps<T>) => {
  const { rowClassName, restRowProps, onClick } = mergeRowProps(getRowProps, row.original, onRowClick);
  return (
    <tr
      {...restRowProps}
      className={cn(
        getRowClassName?.(row.original),
        isHighlightedRow?.(row.original, highlightedRowId)
          ? "bg-gray-100 hover:bg-gray-200"
          : "hover:bg-gray-100",
        rowClassName,
      )}
      onClick={onClick}
    >
      {row.getVisibleCells().map((cell: AppCell<T>) => {
        const column = cell.column.columnDef as TanStackColumn<T>;
        return (
          <td
            key={cell.id}
            className={cn(
              "px-6 py-4 whitespace-nowrap cursor-pointer",
              column.isSelectionColumn && "!px-2 !py-2",
              column.isCopyable && "!cursor-copy",
              column.columnClassName,
            )}
            style={{
              ...(column.autoWidth
                ? {}
                : { width: `calc(var(--col-${cell.column.id}-size) * 1px)` }
              ),
              ...(enableColumnPinning ? getCommonPinningStyles(cell.column) : {}),
            }}
            onClick={async (e) => {
              if (column.isSelectionColumn || column.isCopyable) {
                e.stopPropagation();
              }
              if (column.isCopyable) {
                const cellValue = cell.getValue();
                if (cellValue !== null) {
                  try {
                    await navigator.clipboard.writeText(String(cellValue));
                    toast.success("ID copié dans le presse-papier");
                  }
                  catch {
                    toast.error("Erreur lors de la copie");
                  }
                }
              }
            }}
          >
            {column.isSelectionColumn
              ? (
                <div
                  className="h-full w-full flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              )
              : column.isCopyable
                ? (
                  <TruncateWrapper enableTooltip={column.enableTooltip} tooltipClassName={tooltipClassName}>
                    <CopyableCell>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </CopyableCell>
                  </TruncateWrapper>
                )
                : column.disableTruncate
                  ? (
                    <div className="cursor-pointer w-fit min-w-max">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  )
                  : (
                    <TruncateWrapper enableTooltip={column.enableTooltip} tooltipClassName={tooltipClassName}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TruncateWrapper>
                  )}
          </td>
        );
      })}
    </tr>
  );
};

const sameRenderVersion = (a: readonly unknown[] = [], b: readonly unknown[] = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const areDataRowPropsEqual = <T extends RowData>(prev: DataRowProps<T>, next: DataRowProps<T>) =>
  prev.row.id === next.row.id
  && prev.row.original === next.row.original
  && prev.onRowClick === next.onRowClick
  && prev.getRowClassName === next.getRowClassName
  && prev.isHighlightedRow === next.isHighlightedRow
  && prev.highlightedRowId === next.highlightedRowId
  && prev.tooltipClassName === next.tooltipClassName
  && prev.enableColumnPinning === next.enableColumnPinning
  && prev.getRowProps === next.getRowProps
  && sameRenderVersion(prev.renderVersion, next.renderVersion);

/** DataRow mémoïsé : voir l'option `memoizeRows` de TanStackTable. */
export const MemoizedDataRow = memo(DataRow, areDataRowPropsEqual) as typeof DataRow;
